import { LocationType } from '@/app/(dashboard)/projects/FolderSelectionTree'
import { Icon, cn, Spinner } from '@/dc-shared-web/src'
import { FolderTree } from '@/dc-shared-web/src/hooks/api/folder/types'
import { useIsProjectEditor } from '@/dc-shared-web/src/hooks/api/user/useIsProjectEditor'
import {
  faBriefcase,
  faCheck,
  faFolder,
  faHouseChimney,
  faFileAlt,
  IconDefinition,
} from '@document-crunch/pro-regular-svg-icons'
import { FC, useRef, useEffect } from 'react'

type LocationTreeProps = {
  value: LocationType
  onChange: (value: LocationType) => void
  teamUuid: string
  foldersAndProjects: FolderTree[]
  isLoading: boolean
}

export const LocationTree: FC<LocationTreeProps> = ({
  value,
  onChange,
  teamUuid,
  foldersAndProjects,
  isLoading,
}) => {
  const isProjectEditor = useIsProjectEditor()

  const sortedFoldersAndProjects = foldersAndProjects?.sort((a, b) =>
    a.name.localeCompare(b.name)
  )

  if (isLoading) {
    return (
      <div className='tw-flex tw-justify-center tw-items-center tw-h-full'>
        <Spinner />
      </div>
    )
  }

  return (
    <>
      {!isProjectEditor && (
        <LocationRow
          locationType='dashboard'
          title='Dashboard'
          isSelected={value.type === 'dashboard' && value.id === 'dashboard'}
          isDisabled={false}
          onClick={() =>
            onChange({
              type: 'dashboard',
              id: 'dashboard',
              name: 'Dashboard',
            })
          }
          dataTestId='dashboard-location-row'
        />
      )}
      {sortedFoldersAndProjects?.map((location) => (
        <FolderChildren
          key={location.uuid}
          location={location}
          depth={1}
          selectedLocation={value}
          onLocationSelect={onChange}
          teamUuid={teamUuid}
        />
      ))}
    </>
  )
}

type LocationPathType = 'folder' | 'project' | 'dashboard' | 'document'

const IconByLocationType: Record<LocationPathType, IconDefinition> = {
  folder: faFolder,
  project: faBriefcase,
  dashboard: faHouseChimney,
  document: faFileAlt,
}

type LocationRowProps = {
  locationType: LocationPathType
  title: string
  isSelected: boolean
  isDisabled: boolean
  indentLevel?: number
  onClick: () => void
  dataTestId?: string
}

const LocationRow: FC<LocationRowProps> = ({
  locationType,
  title,
  isSelected,
  isDisabled,
  indentLevel = 0,
  onClick,
  dataTestId,
}) => {
  const rowRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isSelected && rowRef.current) {
      rowRef.current.scrollIntoView({
        behavior: 'instant',
        block: 'center',
        inline: 'center',
      })
    }
  }, [isSelected])

  return (
    <div
      ref={rowRef}
      className={cn(
        'tw-flex tw-justify-between tw-p-2 tw-items-center',
        isSelected && 'tw-bg-primary-300 hover:!tw-bg-primary-300',
        isDisabled
          ? 'tw-text-neutral-300'
          : 'hover:tw-cursor-pointer hover:tw-bg-primary-50'
      )}
      style={{ paddingLeft: `${16 * indentLevel + 8}px` }}
      onClick={isDisabled ? undefined : onClick}
      data-testid={dataTestId}
    >
      <div className='tw-flex tw-items-center'>
        <Icon
          icon={IconByLocationType[locationType]}
          className={cn(
            'tw-h-4 tw-mr-2',
            isDisabled ? 'tw-text-neutral-300' : 'tw-text-foreground'
          )}
        />
        <div className='tw-break-all'>{title}</div>
      </div>
      {isSelected && (
        <Icon icon={faCheck} className='tw-ml-2 tw-text-neutral-900' />
      )}
    </div>
  )
}

type FolderChildrenProps = {
  location: FolderTree
  depth: number
  selectedLocation: LocationType
  onLocationSelect: (location: LocationType) => void
  teamUuid: string
}

const FolderChildren: FC<FolderChildrenProps> = ({
  location,
  depth = 1,
  selectedLocation,
  onLocationSelect,
  teamUuid,
}) => {
  const isSelected =
    selectedLocation.type === location.type &&
    selectedLocation.id === location.uuid

  return (
    <div key={location.uuid}>
      <LocationRow
        locationType={location.type}
        title={location.name}
        isSelected={isSelected}
        isDisabled={false}
        indentLevel={depth}
        onClick={() =>
          onLocationSelect({
            type: location.type,
            id: location.uuid,
            name: location.name,
          })
        }
        dataTestId={`${location.type}-location-row`}
      />
      {location.children?.map((childLocation) => (
        <div key={childLocation.uuid}>
          {childLocation.children?.length > 0 ? (
            <FolderChildren
              location={childLocation}
              depth={depth + 1}
              selectedLocation={selectedLocation}
              onLocationSelect={onLocationSelect}
              teamUuid={teamUuid}
            />
          ) : (
            <LocationRow
              locationType={childLocation.type}
              title={childLocation.name}
              isSelected={
                selectedLocation.type === childLocation.type &&
                selectedLocation.id === childLocation.uuid
              }
              isDisabled={false}
              indentLevel={depth + 1}
              onClick={() =>
                onLocationSelect({
                  type: childLocation.type,
                  id: childLocation.uuid,
                  name: childLocation.name,
                })
              }
              dataTestId={`${childLocation.type}-location-row`}
            />
          )}
        </div>
      ))}
    </div>
  )
}
